import React, { useState, useEffect } from 'react';
import { auth } from '../../services/firebase';
import { DubbingJob } from '../../types/job.types';
import JobCard from './JobCard';
import './JobDashboard.css';

const JobDashboard: React.FC = () => {
  const [jobs, setJobs] = useState<DubbingJob[]>([]);

  const formatDateSafely = (timestamp: any) => {
    if (!timestamp) return new Date();
    if (timestamp.toDate) return timestamp.toDate();
    return new Date(timestamp);
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'processing' | 'completed' | 'failed'>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showOldJobs, setShowOldJobs] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    // Set up auto-refresh every 5 seconds for active jobs
    const interval = setInterval(() => {
      if (jobs.some(job => job.status === 'pending' || job.status === 'processing')) {
        fetchJobs(true);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [jobs]);

  const fetchJobs = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    setError('');

    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/gcp-virtual-production-lab/us-central1/api'}/jobs`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }

      const { data } = await response.json();
      setJobs(data || []);
    } catch (error: any) {
      console.error('Error fetching jobs:', error);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to cancel this job?')) {
      return;
    }

    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/gcp-virtual-production-lab/us-central1/api'}/jobs/${jobId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete job');
      }

      // Remove job from local state
      setJobs(prev => prev.filter(job => job.id !== jobId));
    } catch (error: any) {
      console.error('Error deleting job:', error);
      alert('Failed to cancel job. Please try again.');
    }
  };

  const handleViewDetails = (jobId: string) => {
    console.log('View details for job:', jobId);
    // TODO: Open modal or navigate to details page
  };

  const filteredJobs = jobs.filter(job => {
    // Filter by status
    if (filter !== 'all' && job.status !== filter) return false;
    
    // Filter old jobs (older than 7 days) unless explicitly showing them
    if (!showOldJobs) {
      const jobDate = formatDateSafely(job.createdAt);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      if (jobDate < sevenDaysAgo && job.status === 'completed') {
        return false;
      }
    }
    
    return true;
  });
  
  // Count old completed jobs
  const oldJobsCount = jobs.filter(job => {
    if (job.status !== 'completed') return false;
    const jobDate = formatDateSafely(job.createdAt);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return jobDate < sevenDaysAgo;
  }).length;

  const getJobStats = () => {
    return {
      total: jobs.length,
      pending: jobs.filter(j => j.status === 'pending').length,
      processing: jobs.filter(j => j.status === 'processing').length,
      completed: jobs.filter(j => j.status === 'completed').length,
      failed: jobs.filter(j => j.status === 'failed').length
    };
  };

  const stats = getJobStats();

  if (loading && jobs.length === 0) {
    return (
      <div className="job-dashboard-loading">
        <div className="spinner"></div>
        <p>Loading your dubbing jobs...</p>
      </div>
    );
  }

  return (
    <div className="job-dashboard">
      <div className="dashboard-header">
        <h2>Your Dubbing Jobs</h2>
        <button 
          className="refresh-button"
          onClick={() => {
            fetchJobs();
            setRefreshTrigger(prev => prev + 1);
          }}
          disabled={refreshing}
        >
          {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {error && (
        <div className="dashboard-error">
          ⚠️ {error}
        </div>
      )}

      <div className="job-stats">
        <div className="stat-card">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total Jobs</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#f59e0b' }}>{stats.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#3b82f6' }}>{stats.processing}</span>
          <span className="stat-label">Processing</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#10b981' }}>{stats.completed}</span>
          <span className="stat-label">Completed</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#ef4444' }}>{stats.failed}</span>
          <span className="stat-label">Failed</span>
        </div>
      </div>

      <div className="job-filters">
        <button
          className={`filter-button ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({stats.total})
        </button>
        <button
          className={`filter-button ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending ({stats.pending})
        </button>
        <button
          className={`filter-button ${filter === 'processing' ? 'active' : ''}`}
          onClick={() => setFilter('processing')}
        >
          Processing ({stats.processing})
        </button>
        <button
          className={`filter-button ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          Completed ({stats.completed})
        </button>
        <button
          className={`filter-button ${filter === 'failed' ? 'active' : ''}`}
          onClick={() => setFilter('failed')}
        >
          Failed ({stats.failed})
        </button>
      </div>

      {oldJobsCount > 0 && (
        <div className="old-jobs-toggle" style={{ textAlign: 'center', margin: '10px 0' }}>
          <button
            className={`toggle-button ${showOldJobs ? 'active' : ''}`}
            onClick={() => setShowOldJobs(!showOldJobs)}
            style={{
              padding: '8px 16px',
              background: showOldJobs ? '#e74c3c' : '#95a5a6',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer'
            }}
          >
            {showOldJobs ? '🙈 Hide' : '👁️ Show'} old completed jobs ({oldJobsCount} older than 7 days)
          </button>
        </div>
      )}

      <div className="job-list">
        {filteredJobs.length === 0 ? (
          <div className="no-jobs">
            <p>
              {filter === 'all' 
                ? "You haven't created any dubbing jobs yet."
                : `No ${filter} jobs found.`}
            </p>
            {filter === 'all' && (
              <p className="no-jobs-hint">
                Upload a video and configure dubbing settings to get started!
              </p>
            )}
          </div>
        ) : (
          filteredJobs.map(job => (
            <JobCard
              key={job.id}
              job={job}
              onViewDetails={handleViewDetails}
              onDelete={handleDeleteJob}
              refreshTrigger={refreshTrigger}
              onJobUpdated={() => {
                fetchJobs();
                setRefreshTrigger(prev => prev + 1);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default JobDashboard;